import { computed, ref, readonly } from 'vue';

const storageKey = 'beedigit.ui.theme';
const ownerKey = 'beedigit.ui.theme.user';
const current = ref('light');
const preference = ref('system');
let systemTheme;
const isLight = computed(() => current.value === 'light');
let initialized = false;
let saveProfile = null;
let profileUserId = null;
let revision = 0;
let localSelection = 0;
let saves = Promise.resolve();
let pendingTheme = null;
const receivedSelections = new Map();
const normalize = (value) => (['light', 'dark'].includes(value) ? value : 'system');

function apply(value) {
  preference.value = normalize(value);
  render();
}

function render() {
  const systemAppearance = systemTheme?.matches ? 'dark' : 'light';
  current.value = preference.value === 'system' ? systemAppearance : preference.value;
  document.documentElement.dataset.uiTheme = current.value;
  document.documentElement.style.colorScheme = current.value;
}

export function configureUiThemeProfile(userId, theme, save) {
  if (!userId) {
    saveProfile = null;
    profileUserId = null;
    revision += 1;
    return;
  }
  saveProfile = save;
  if (profileUserId === userId) return;
  profileUserId = userId;
  revision += 1;
  const selectedWhileLoading = pendingTheme;
  pendingTheme = null;
  let migrate = null;
  try {
    const owner = localStorage.getItem(ownerKey);
    const cached = localStorage.getItem(storageKey);
    if (!theme && (!owner || owner === userId) && ['light', 'dark', 'system'].includes(cached)) {
      migrate = cached;
    }
  } catch {
    // A fresh device restores only the server preference.
  }
  apply(selectedWhileLoading ?? receivedSelections.get(userId) ?? theme ?? migrate);
  receivedSelections.clear();
  try {
    localStorage.setItem(storageKey, preference.value);
    localStorage.setItem(ownerKey, userId);
    localStorage.setItem(`${storageKey}.${userId}`, preference.value);
  } catch {
    // The server profile still works without browser storage.
  }
  if (selectedWhileLoading || migrate) useUiTheme().set(selectedWhileLoading ?? migrate);
}

export function initializeUiTheme() {
  if (initialized) return;
  initialized = true;
  systemTheme = window.matchMedia?.('(prefers-color-scheme: dark)');
  systemTheme?.addEventListener('change', () => {
    if (preference.value === 'system') {
      render();
    }
  });
  try {
    apply(localStorage.getItem(storageKey));
  } catch {
    apply('system');
  }
  window.addEventListener('storage', (event) => {
    if (event.storageArea && event.storageArea !== localStorage) return;
    if (event.key === ownerKey) return;
    const accountKey = event.key?.startsWith(`${storageKey}.`) ? event.key : null;
    if (!accountKey && event.key !== storageKey && event.key !== null) return;
    const owner = accountKey
      ? accountKey.slice(storageKey.length + 1)
      : localStorage.getItem(ownerKey);
    if (profileUserId && owner !== profileUserId) return;
    if (event.key && localStorage.getItem(event.key) !== event.newValue) return;
    const selected = normalize(event.newValue);
    if (owner && !profileUserId) receivedSelections.set(owner, selected);
    // Equal values do not establish event order (dark -> light -> dark).
    // Only a different selection supersedes a queued local save.
    if (selected === preference.value) return;
    revision += 1;
    apply(selected);
  });
}

export function useUiTheme() {
  return {
    current: readonly(current),
    preference: readonly(preference),
    isLight,
    set(theme) {
      apply(theme);
      const selected = preference.value;
      const save = saveProfile;
      const userId = profileUserId;
      if (!save) pendingTheme = selected;
      const selectionRevision = ++revision;
      const selectedLocally = ++localSelection;
      let storedSelection = false;
      try {
        if (profileUserId) localStorage.setItem(ownerKey, profileUserId);
        localStorage.setItem(storageKey, selected);
        if (userId) {
          localStorage.setItem(`${storageKey}.${userId}`, selected);
          storedSelection = true;
        }
      } catch {
        // Switching still works when browser storage is blocked.
      }
      if (save) {
        saves = saves
          .catch(() => undefined)
          .then(async () => {
            const persist = async () => {
              if (selectionRevision !== revision) return;
              try {
                if (storedSelection && localStorage.getItem(`${storageKey}.${userId}`) !== selected)
                  return;
              } catch {
                // Storage can be disabled while the profile remains available.
              }
              await save(selected);
              return true;
            };
            // Origin-wide locks serialize authenticated writes across tabs.
            if (window.navigator.locks) {
              await window.navigator.locks.request(`${storageKey}.${userId}`, persist);
            } else {
              const written = await persist();
              let lastWritten = selected;
              // Older browsers lack Web Locks. Repair an out-of-order response
              // against the latest account-specific selection before finishing.
              while (
                written &&
                storedSelection &&
                profileUserId === userId &&
                selectedLocally === localSelection
              ) {
                let latest;
                try {
                  latest = localStorage.getItem(`${storageKey}.${userId}`);
                } catch {
                  break;
                }
                if (!['light', 'dark', 'system'].includes(latest) || latest === lastWritten) break;
                await save(latest);
                lastWritten = latest;
              }
            }
          });
      }
    },
  };
}
